export type PublishLoadOutcome = "idle" | "success" | "error";

/** How long the header shows success/error after polling settles (URL + managed flows). */
export const PUBLISH_HEADER_OUTCOME_MS = 2800;

/** Prefer `error` over `success` if both sources disagree (e.g. overlapping updates). */
export function mergePublishLoadOutcomes(
  a: PublishLoadOutcome,
  b: PublishLoadOutcome
): PublishLoadOutcome {
  if (a === "error" || b === "error") return "error";
  if (a === "success" || b === "success") return "success";
  return "idle";
}

/**
 * Shape returned by `checkCampaignUpdate` / campaign update metadata (Dynamo).
 * Terminal rows use status !== "PROCESSING"; failures set `error` (see completeCampaignUpdate).
 */
export type CampaignPublishCheckResponse = {
  status?: string;
  update_status?: string;
  error?: string | Record<string, unknown> | null;
};

function nonEmptyError(e: CampaignPublishCheckResponse["error"]): boolean {
  if (e == null) return false;
  if (typeof e === "string") return e.trim().length > 0;
  return Object.keys(e).length > 0;
}

export function formatPublishUpdateErrorMessage(
  res: CampaignPublishCheckResponse | null | undefined
): string {
  const e = res?.error;
  if (typeof e === "string" && e.trim()) return e.trim();
  if (e != null && typeof e === "object" && Object.keys(e).length > 0) {
    try {
      return JSON.stringify(e);
    } catch {
      return "Unable to complete campaign publish";
    }
  }
  return "Unable to complete campaign publish";
}

/** True when the update finished with an error payload (not PROCESSING). */
export function publishCheckResponseFailed(
  res: CampaignPublishCheckResponse | null | undefined
): boolean {
  if (res == null) return true;
  if (res.status === "PROCESSING") return false;
  return nonEmptyError(res.error);
}

/** `usePolling` yields `false` on max runs; otherwise the last check payload. */
export function publishPollResultIndicatesFailure(
  result: false | CampaignPublishCheckResponse | null | undefined
): boolean {
  if (result === false || result == null) return true;
  return publishCheckResponseFailed(result);
}
